/**
 * O autocadastro (0150) visto pela tela: onde a pessoa está, os dados da clínica e o
 * pagamento.
 *
 * Porta própria, no molde da autenticação. No ar, fala com o banco (as RPCs
 * `autocadastro_*`) e com a borda `autocadastro`; sem servidor, a demonstração
 * mostra os passos e diz, no último, que ali não se abre clínica nem se cobra
 * ninguém. Os testes trocam a porta com `definirAutocadastro`.
 */

import { DOCUMENTOS } from '@/data/juridico';
import { supabase, temSupabase } from '@/supabase';

export interface PlanoDoCadastro {
    codigo: string;
    nome: string;
    precoCentavos: number;
}

export type FormaDePagamento = 'cartao' | 'pix_automatico';

export interface PendenteDoCadastro {
    id: string;
    nomeClinica: string;
    nome: string;
    documento: string;
    whatsapp: string;
    plano: string | null;
    formaDePagamento: FormaDePagamento | null;
    aguardandoPagamento: boolean;
    cartaoFinal: string | null;
    erro: string | null;
    expirado: boolean;
}

export interface SituacaoDoCadastro {
    emailConfirmado: boolean;
    /** É membro ativo de alguma clínica — mesmo que ela esteja fechada. */
    temClinica: boolean;
    ehOperador: boolean;
    jaUsouTeste: boolean;
    pendente: PendenteDoCadastro | null;
}

export interface DadosDaClinicaNova {
    nomeClinica: string;
    nome: string;
    documento: string;
    whatsapp: string;
    plano: string;
}

export interface VersoesAceitas {
    termosVersao: string;
    privacidadeVersao: string;
}

/**
 * **Contrato alterado em 22/09/2026, com o cartão passando para a Stripe:** o número do
 * cartão não vem mais até aqui. O navegador tokeniza pelo Stripe.js e manda só o `pm_…`
 * (ver `src/data/stripeNoNavegador.ts`). O endereço do titular também sai: ele existia
 * porque o Asaas o pedia junto do cartão, e agora o CEP vai à Stripe como
 * `billing_details`, de dentro do navegador.
 */
export interface PagamentoNoCartao extends VersoesAceitas {
    pendente: string;
    /** O `pm_…` do Stripe.js. Nada de número, validade ou CVV atravessa esta porta. */
    meioDePagamento: string;
}

/** O que o navegador precisa para montar o campo do cartão. */
export interface PreparoDoCartao {
    chavePublicavel: string;
    ambiente: 'sandbox' | 'producao';
}

export type Resultado<T extends object> = ({ ok: true } & T) | { ok: false; erro: string };

export interface PortaDoAutocadastro {
    planos(): Promise<PlanoDoCadastro[]>;
    situacao(): Promise<SituacaoDoCadastro>;
    preparar(dados: DadosDaClinicaNova): Promise<Resultado<{ pendenteId: string }>>;
    /** A chave publicável da Stripe, para o campo do cartão. Só a borda sabe qual é. */
    preparoDoCartao(): Promise<Resultado<PreparoDoCartao>>;
    pagarNoCartao(pedido: PagamentoNoCartao): Promise<Resultado<{ clinicaId: string; testeAte: string; cartaoFinal: string }>>;
    gerarPix(pedido: VersoesAceitas & { pendente: string }): Promise<Resultado<{
        copiaECola: string | null; imagemBase64: string | null; expiraEm: string | null; valorCentavos: number;
    }>>;
    cancelarTeste(clinicaId: string): Promise<Resultado<{ testeAte: string; removidaNoGateway: boolean }>>;
}

/**
 * As versões que a pessoa aceita são as do texto que a tela mostra (`src/data/juridico.ts`,
 * publicado em /termos-de-uso e /politica-de-privacidade). Antes vinham de variável de build,
 * que podia divergir do texto exibido — e o aceite gravaria uma versão que ninguém leu.
 * Em produção a borda só aceita as publicadas: ao ligar o cadastro, `TERMOS_VERSAO` e
 * `PRIVACIDADE_VERSAO` recebem estas mesmas versões.
 */
export const VERSOES_DOS_TERMOS: VersoesAceitas = {
    termosVersao: DOCUMENTOS.termos.versao,
    privacidadeVersao: DOCUMENTOS.privacidade.versao,
};

const FALHA = 'Não foi possível concluir. Tente de novo.';

/** A borda devolve o erro no corpo; o cliente do Supabase só dá a mensagem genérica. */
async function mensagemDaBorda(erro: unknown): Promise<string | undefined> {
    const contexto = (erro as { context?: unknown })?.context;
    if (contexto instanceof Response) {
        try {
            const corpo = await contexto.json();
            if (typeof corpo?.erro === 'string') return corpo.erro;
        } catch {
            /* Sem corpo legível: fica a mensagem de quem chamou. */
        }
    }
    return undefined;
}

async function borda<T extends object>(
    nome: string, corpo: object, falha: string, ler: (r: Record<string, unknown>) => T,
): Promise<Resultado<T>> {
    if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
    const { data, error } = await supabase.functions.invoke(nome, { body: corpo });
    if (error) return { ok: false, erro: (await mensagemDaBorda(error)) ?? falha };
    const r = (data ?? {}) as Record<string, unknown>;
    if (typeof r.erro === 'string') return { ok: false, erro: r.erro };
    return { ok: true as const, ...ler(r) };
}

const textoOuNulo = (v: unknown) => (typeof v === 'string' && v ? v : null);

export const autocadastroSupabase: PortaDoAutocadastro = {
    async planos() {
        if (!supabase) return [];
        const { data, error } = await supabase
            .from('planos')
            .select('codigo, nome, preco_centavos')
            .eq('ativo', true)
            .not('preco_centavos', 'is', null)
            .order('preco_centavos');
        if (error) throw new Error(error.message);
        return (data ?? []).map((p) => ({ codigo: String(p.codigo), nome: String(p.nome), precoCentavos: Number(p.preco_centavos) }));
    },

    async situacao() {
        if (!supabase) throw new Error('Servidor não configurado.');
        const { data, error } = await supabase.rpc('autocadastro_situacao');
        if (error) throw new Error(error.message);
        const s = (data ?? {}) as Record<string, unknown>;
        const p = s.pendente as Record<string, unknown> | null | undefined;
        return {
            emailConfirmado: s.emailConfirmado === true,
            temClinica: s.temClinica === true,
            ehOperador: s.ehOperador === true,
            jaUsouTeste: s.jaUsouTeste === true,
            pendente: p ? {
                id: String(p.id),
                nomeClinica: String(p.nomeClinica ?? ''),
                nome: String(p.nome ?? ''),
                documento: String(p.documento ?? ''),
                whatsapp: String(p.whatsapp ?? ''),
                plano: textoOuNulo(p.plano),
                formaDePagamento: p.formaDePagamento === 'cartao' || p.formaDePagamento === 'pix_automatico' ? p.formaDePagamento : null,
                aguardandoPagamento: p.aguardandoPagamento === true,
                cartaoFinal: textoOuNulo(p.cartaoFinal),
                erro: textoOuNulo(p.erro),
                expirado: p.expirado === true,
            } : null,
        };
    },

    async preparar(dados) {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        const { data, error } = await supabase.rpc('autocadastro_preparar', { p_dados: dados });
        if (error) return { ok: false, erro: error.message || FALHA };
        return { ok: true, pendenteId: String(data) };
    },

    preparoDoCartao() {
        return borda('autocadastro/preparo-do-cartao', {}, 'O pagamento por cartão está indisponível no momento.', (r) => ({
            chavePublicavel: String(r.chavePublicavel ?? ''),
            ambiente: r.ambiente === 'producao' ? 'producao' as const : 'sandbox' as const,
        }));
    },

    pagarNoCartao(pedido) {
        return borda('autocadastro/cartao', pedido, 'O pagamento não foi concluído. Nada foi cobrado; tente de novo.', (r) => ({
            clinicaId: String(r.clinica), testeAte: String(r.testeAte), cartaoFinal: String(r.cartaoFinal),
        }));
    },

    gerarPix(pedido) {
        return borda('autocadastro/pix', pedido, 'Não foi possível gerar o Pix. Tente de novo.', (r) => ({
            copiaECola: textoOuNulo(r.copiaECola),
            imagemBase64: textoOuNulo(r.imagemBase64),
            expiraEm: textoOuNulo(r.expiraEm),
            valorCentavos: Number(r.valorCentavos),
        }));
    },

    cancelarTeste(clinicaId) {
        return borda('autocadastro/cancelar-teste', { clinica: clinicaId }, 'Não foi possível cancelar o teste.', (r) => ({
            testeAte: String(r.testeAte), removidaNoGateway: r.removidaNoGateway === true,
        }));
    },
};

const NA_DEMONSTRACAO = 'Na demonstração não se abre clínica nem se cobra ninguém: o cadastro com pagamento precisa do servidor.';

export const autocadastroDemo: PortaDoAutocadastro = {
    /* Os preços decididos em 13/09/2026, para a tela mostrar o que o cadastro real mostra. */
    async planos() {
        return [
            { codigo: 'essencial', nome: 'Essencial', precoCentavos: 19900 },
            { codigo: 'profissional', nome: 'Profissional', precoCentavos: 59900 },
        ];
    },
    async situacao() {
        return { emailConfirmado: false, temClinica: false, ehOperador: false, jaUsouTeste: false, pendente: null };
    },
    async preparar() {
        return { ok: false, erro: NA_DEMONSTRACAO };
    },
    async preparoDoCartao() {
        return { ok: false, erro: NA_DEMONSTRACAO };
    },
    async pagarNoCartao() {
        return { ok: false, erro: NA_DEMONSTRACAO };
    },
    async gerarPix() {
        return { ok: false, erro: NA_DEMONSTRACAO };
    },
    async cancelarTeste() {
        return { ok: false, erro: NA_DEMONSTRACAO };
    },
};

/**
 * Se o autocadastro está ligado neste build (`VITE_AUTOCADASTRO=1`). Desligado, criar
 * conta continua sendo aceitar convite: o cadastro com pagamento só vai ao ar com os
 * termos publicados e o Asaas liberado (decisão de 15/09/2026). Lido na hora, para
 * os testes poderem ligar com `vi.stubEnv`.
 */
export function autocadastroLigado(): boolean {
    return String(import.meta.env.VITE_AUTOCADASTRO ?? '') === '1';
}

let porta: PortaDoAutocadastro = temSupabase() ? autocadastroSupabase : autocadastroDemo;

/** Troca a porta. Um lugar só, para os testes. */
export function definirAutocadastro(nova: PortaDoAutocadastro): void {
    porta = nova;
}

export function autocadastro(): PortaDoAutocadastro {
    return porta;
}
